/**
 * Enlace interno: un `<a href>` de verdad que navega sin recargar.
 *
 * Es un `<a>` y no un `<button>` a propósito: el usuario puede copiar el
 * enlace, abrirlo en otra pestaña con Ctrl+clic o con el botón central, y el
 * navegador lo anuncia como enlace. Todo eso se pierde con un botón que llama
 * a `navegarA` en su `onClick`, que es el atajo que suele acabar en el código.
 */
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'
import { navegarA } from './historial.js'
import { construirRuta, type Vista } from './rutas.js'

interface PropsEnlace extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  vista: Vista
  parametros?: Readonly<Record<string, string>>
  /** Estado que viaja en la entrada del historial: de dónde se abrió esto. */
  estado?: unknown
  children: ReactNode
}

/** Un clic que el navegador debe manejar él: nueva pestaña, ventana, descarga. */
function esClicDelNavegador(evento: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    evento.defaultPrevented ||
    evento.button !== 0 ||
    evento.metaKey ||
    evento.ctrlKey ||
    evento.shiftKey ||
    evento.altKey
  )
}

export function Enlace({ vista, parametros, estado, children, onClick, ...resto }: PropsEnlace) {
  const destino = construirRuta(vista, parametros)

  return (
    <a
      href={destino}
      onClick={(evento) => {
        onClick?.(evento)
        if (esClicDelNavegador(evento)) return
        evento.preventDefault()
        navegarA(destino, estado === undefined ? {} : { state: estado })
      }}
      {...resto}
    >
      {children}
    </a>
  )
}

/** Navegación imperativa, para después de una mutación. */
export function irA(vista: Vista, parametros?: Readonly<Record<string, string>>, estado?: unknown): void {
  navegarA(construirRuta(vista, parametros), estado === undefined ? {} : { state: estado })
}
